import React, { useCallback, useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import {
  Edit2, Plus, X, TrendingUp, DollarSign, PiggyBank, Info,
  LayoutDashboard, Package, Receipt, Wallet, ShoppingCart, Trash2,
} from 'lucide-react';
import './RyanRepresentatives.css';

const tok = () => localStorage.getItem('accessToken') || '';
const api = (path: string, opts?: RequestInit) =>
  fetch(`/api/ryan${path}`, { ...opts, headers: { Authorization: `Bearer ${tok()}`, 'Content-Type': 'application/json', ...(opts?.headers || {}) } });
const cents = (c: number) => `R$ ${(c / 100).toFixed(2).replace('.', ',')}`;
const fmtDate = (d: string | null) => d ? new Date(d).toLocaleDateString('pt-BR', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';

interface Product { _id: string; title: string; }

interface CommissionTier { minSales: number; percent: number; }

const DEFAULT_COMMISSION_TIERS: CommissionTier[] = [
  { minSales: 1, percent: 20 },
  { minSales: 10, percent: 30 },
  { minSales: 20, percent: 40 },
  { minSales: 35, percent: 50 },
];

interface CycleInfo { number: number; openedAt: string; }

interface RepProduct {
  productId: string;
  title: string;
  periodo: number | null;
  freeAccess: boolean;
  salesCount: number;
  salesCountThisCycle: number;
  totalRevenueCents: number;
  currentTierPercent: number;
  // Piso garantido pro ciclo vigente, herdado do ciclo anterior — null quando
  // não há nenhum (tier atual é 100% orgânico pelas vendas deste ciclo).
  carryOverPercent: number | null;
  totalEarnedCents: number;
  totalPaidCents: number;
  totalPendingCents: number;
}

interface Representative {
  _id: string;
  name: string;
  email: string;
  referralCode: string;
  active: boolean;
  hasAccount: boolean;
  commissionTiers: CommissionTier[];
  products: RepProduct[];
}

interface TierStep { minSales: number; percent: number; reached: boolean; current: boolean; }

interface DetailProduct extends RepProduct { tiers: TierStep[]; }

interface PayoutEntry {
  _id: string;
  amountCents: number;
  salesCount: number;
  paidAt: string | null;
  note: string;
  legacy: boolean;
}

interface DetailSummary {
  totalSales: number;
  totalRevenueCents: number;
  totalEarnedCents: number;
  totalPendingCents: number;
  totalProfitCents: number;
}

interface RepresentativeDetail {
  _id: string;
  name: string;
  email: string;
  referralCode: string;
  active: boolean;
  hasAccount: boolean;
  summary: DetailSummary;
  products: DetailProduct[];
  payoutHistory: PayoutEntry[];
  currentCycle: CycleInfo;
}

type DetailTab = 'overview' | 'products' | 'payments';

interface RankingRow { representativeId: string; name: string; salesCount: number; position: number; }

interface Summary {
  totalSales: number;
  totalRevenueCents: number;
  totalCommissionCents: number;
  netProfitCents: number;
  ranking: RankingRow[];
}

interface FormState {
  name: string;
  email: string;
  referralCode: string;
  selected: Record<string, boolean>;
  freeAccess: Record<string, boolean>;
  commissionTiers: CommissionTier[];
}

const emptyForm = (): FormState => ({ name: '', email: '', referralCode: '', selected: {}, freeAccess: {}, commissionTiers: DEFAULT_COMMISSION_TIERS });

interface UserSuggestion { _id: string; email: string; username: string; }

const RyanRepresentatives: React.FC = () => {
  const [summary, setSummary] = useState<Summary | null>(null);
  const [reps, setReps] = useState<Representative[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentCycle, setCurrentCycle] = useState<CycleInfo | null>(null);
  const [confirmCloseCycle, setConfirmCloseCycle] = useState(false);
  const [closingCycle, setClosingCycle] = useState(false);

  // Resetar ciclos — ferramenta de manutenção rara/global (afeta todos os
  // embaixadores de uma vez), por isso confirmação tipada (mesmo padrão da
  // Zona de Risco em Configurações) em vez do confirm inline de 1 clique já
  // usado pra fechar ciclo/cancelar pagamento.
  const [resetCyclesOpen, setResetCyclesOpen] = useState(false);
  const [resetConfirmText, setResetConfirmText] = useState('');
  const [resetting, setResetting] = useState(false);
  const [resetError, setResetError] = useState<string | null>(null);

  const [rankingOpen, setRankingOpen] = useState(false);

  const [detailId, setDetailId] = useState<string | null>(null);
  const [detail, setDetail] = useState<RepresentativeDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailTab, setDetailTab] = useState<DetailTab>('overview');
  const [productPeriodFilter, setProductPeriodFilter] = useState<number | null>(null);
  const [payNote, setPayNote] = useState('');
  const [paying, setPaying] = useState(false);
  const [cancelingPayoutId, setCancelingPayoutId] = useState<string | null>(null);
  const [confirmCancelId, setConfirmCancelId] = useState<string | null>(null);

  const [formOpen, setFormOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<FormState>(emptyForm());
  const [saving, setSaving] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  // Código de indicação editável — troca reabre um risco (links já
  // compartilhados com o código atual param de funcionar), então exige uma
  // segunda confirmação explícita antes de salvar. editingOriginalCode
  // guarda o valor com que o modal foi aberto, pra comparar contra o que
  // está no form agora (detecta mudança mesmo que o admin edite e volte
  // pro valor original, sem exigir confirmação à toa).
  const [editingOriginalCode, setEditingOriginalCode] = useState('');
  const [confirmCodeChange, setConfirmCodeChange] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // ── Autocomplete de email no cadastro (novo embaixador) ──
  const [emailSuggestions, setEmailSuggestions] = useState<UserSuggestion[]>([]);
  const [emailSuggestionsOpen, setEmailSuggestionsOpen] = useState(false);
  const emailSearchTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Evita disparar uma busca nova logo depois de escolher uma sugestão
  // (selecionar já preenche o email — sem isso, o efeito de debounce abaixo
  // reagiria a essa mudança como se fosse digitação e reabriria a lista).
  const skipNextEmailSearch = useRef(false);

  useEffect(() => {
    // Campo de email só existe na tela pra cadastro novo (!editingId) — editar
    // embaixador existente também passa por `setForm` (com o email atual dele
    // já preenchido), mas isso não deveria disparar uma busca.
    if (editingId) return;
    if (skipNextEmailSearch.current) { skipNextEmailSearch.current = false; return; }
    const q = form.email.trim();
    if (q.length < 3) { setEmailSuggestions([]); setEmailSuggestionsOpen(false); return; }
    if (emailSearchTimer.current) clearTimeout(emailSearchTimer.current);
    emailSearchTimer.current = setTimeout(async () => {
      const r = await api(`/admin/users/search?q=${encodeURIComponent(q)}`);
      if (r.ok) {
        const d = await r.json();
        setEmailSuggestions(d.users || []);
        setEmailSuggestionsOpen((d.users || []).length > 0);
      }
    }, 350);
    return () => { if (emailSearchTimer.current) clearTimeout(emailSearchTimer.current); };
  }, [form.email, editingId]);

  const selectEmailSuggestion = (u: UserSuggestion) => {
    skipNextEmailSearch.current = true;
    setForm(f => ({ ...f, email: u.email, name: u.username ? u.username : f.name }));
    setEmailSuggestions([]);
    setEmailSuggestionsOpen(false);
  };

  const loadSummary = useCallback(async () => {
    const r = await api('/admin/representatives/summary');
    if (r.ok) setSummary(await r.json());
  }, []);

  const loadReps = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api('/admin/representatives');
      if (r.ok) {
        const d = await r.json();
        setReps(d.representatives || []);
        setCurrentCycle(d.currentCycle || null);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  const loadDetail = useCallback(async (id: string) => {
    setDetailLoading(true);
    try {
      const r = await api(`/admin/representatives/${id}`);
      if (r.ok) setDetail(await r.json());
    } finally {
      setDetailLoading(false);
    }
  }, []);

  useEffect(() => { loadSummary(); loadReps(); }, [loadSummary, loadReps]);
  useEffect(() => {
    api('/admin/products').then(r => r.ok ? r.json() : []).then(d => setProducts(Array.isArray(d) ? d : (d.products || [])));
  }, []);

  const openDetail = (id: string) => {
    setDetailId(id);
    setDetail(null);
    setPayNote('');
    setDetailTab('overview');
    setProductPeriodFilter(null);
    setConfirmCancelId(null);
    loadDetail(id);
  };
  const closeDetail = () => { setDetailId(null); setDetail(null); };

  const refreshAll = useCallback(() => {
    loadSummary();
    loadReps();
    if (detailId) loadDetail(detailId);
  }, [loadSummary, loadReps, loadDetail, detailId]);

  const handleCloseCycle = async () => {
    setClosingCycle(true);
    try {
      const r = await api('/admin/commission-cycles/close', { method: 'POST' });
      if (r.ok) {
        setConfirmCloseCycle(false);
        refreshAll();
      }
    } finally {
      setClosingCycle(false);
    }
  };

  const openResetCycles = () => {
    setResetConfirmText('');
    setResetError(null);
    setResetCyclesOpen(true);
  };
  const closeResetCycles = () => {
    setResetCyclesOpen(false);
    setResetConfirmText('');
    setResetError(null);
  };

  // Apaga só o bookkeeping de ciclo (RyanCommissionCycle/CycleResult/
  // CarryOver) — nunca RyanCommissionEntry/Payout, que são o ganho real de
  // cada venda e continuam intactos. O backend decide o número do novo ciclo
  // (não é sempre 1 — só quando não existe nenhuma comissão no histórico,
  // pra nunca colidir cycleNumber com uma entry antiga real).
  const handleResetCycles = async () => {
    setResetting(true);
    setResetError(null);
    try {
      const r = await api('/admin/commission-cycles/reset', { method: 'POST' });
      const d = await r.json().catch(() => ({} as { message?: string }));
      if (!r.ok) { setResetError(d.message || 'Erro ao resetar ciclos.'); return; }
      closeResetCycles();
      setConfirmCloseCycle(false);
      refreshAll();
    } catch {
      setResetError('Erro de conexão.');
    } finally {
      setResetting(false);
    }
  };

  const openCreate = () => {
    setEditingId(null);
    setForm(emptyForm());
    setErrorMsg(null);
    setEditingOriginalCode('');
    setConfirmCodeChange(false);
    setConfirmDelete(false);
    setFormOpen(true);
  };

  const openEdit = (rep: Representative, e?: React.MouseEvent) => {
    e?.stopPropagation();
    setEditingId(rep._id);
    setForm({
      name: rep.name,
      email: rep.email,
      referralCode: rep.referralCode,
      selected: Object.fromEntries(rep.products.map(p => [p.productId, true])),
      freeAccess: Object.fromEntries(rep.products.map(p => [p.productId, p.freeAccess])),
      commissionTiers: rep.commissionTiers && rep.commissionTiers.length > 0 ? rep.commissionTiers : DEFAULT_COMMISSION_TIERS,
    });
    setErrorMsg(null);
    setEditingOriginalCode(rep.referralCode);
    setConfirmCodeChange(false);
    setConfirmDelete(false);
    setFormOpen(true);
  };

  const closeForm = () => {
    setFormOpen(false);
    setEditingId(null);
    setEmailSuggestions([]);
    setEmailSuggestionsOpen(false);
    setConfirmCodeChange(false);
    setConfirmDelete(false);
  };

  // true só em edição (criação nunca precisa do aviso — não há link antigo
  // pra quebrar) e só quando o valor normalizado difere do original.
  const codeChanged = editingId !== null && form.referralCode.trim().toUpperCase() !== editingOriginalCode;

  // Exclusão real — backend bloqueia (409) se o embaixador já tiver
  // qualquer comissão no histórico, sugerindo desativar em vez disso.
  const handleDelete = async () => {
    if (!editingId) return;
    setDeleting(true);
    setErrorMsg(null);
    try {
      const r = await api(`/admin/representatives/${editingId}`, { method: 'DELETE' });
      const d = await r.json().catch(() => ({} as { message?: string }));
      if (!r.ok) { setErrorMsg(d.message || 'Erro ao excluir embaixador.'); setConfirmDelete(false); return; }
      closeForm();
      refreshAll();
    } catch {
      setErrorMsg('Erro de conexão.');
    } finally {
      setDeleting(false);
    }
  };

  const toggleProduct = (id: string) => {
    setForm(f => ({ ...f, selected: { ...f.selected, [id]: !f.selected[id] } }));
  };

  const toggleFreeAccess = (id: string) => {
    setForm(f => ({ ...f, freeAccess: { ...f.freeAccess, [id]: !f.freeAccess[id] } }));
  };

  const updateTier = (i: number, patch: Partial<CommissionTier>) => {
    setForm(f => ({ ...f, commissionTiers: f.commissionTiers.map((t, idx) => idx === i ? { ...t, ...patch } : t) }));
  };
  const addTier = () => {
    setForm(f => ({ ...f, commissionTiers: [...f.commissionTiers, { minSales: 0, percent: 0 }] }));
  };
  const removeTier = (i: number) => {
    setForm(f => ({ ...f, commissionTiers: f.commissionTiers.filter((_, idx) => idx !== i) }));
  };

  const handleSave = async () => {
    if (!form.name.trim() || (!editingId && !form.email.trim())) return;
    setSaving(true);
    setErrorMsg(null);
    const assignedProducts = Object.keys(form.selected)
      .filter(id => form.selected[id])
      .map(id => ({ productId: id, freeAccess: !!form.freeAccess[id] }));
    try {
      const path = editingId ? `/admin/representatives/${editingId}` : '/admin/representatives';
      const method = editingId ? 'PATCH' : 'POST';
      const body = editingId
        ? { name: form.name.trim(), referralCode: form.referralCode.trim(), assignedProducts, commissionTiers: form.commissionTiers }
        : { name: form.name.trim(), email: form.email.trim(), referralCode: form.referralCode.trim() || undefined, assignedProducts, commissionTiers: form.commissionTiers };
      const r = await api(path, { method, body: JSON.stringify(body) });
      const d = await r.json();
      if (!r.ok) { setErrorMsg(d.message || 'Erro ao salvar embaixador.'); return; }
      closeForm();
      refreshAll();
    } catch {
      setErrorMsg('Erro de conexão.');
    } finally {
      setSaving(false);
    }
  };

  // Clique no botão principal do modal — se o código de indicação mudou e
  // ainda não mostrou o aviso, só revela o aviso (não salva ainda); um
  // segundo clique (já com o aviso visível) prossegue com o save de fato.
  const handleSaveClick = () => {
    if (codeChanged && !confirmCodeChange) {
      setConfirmCodeChange(true);
      return;
    }
    handleSave();
  };

  const handleToggleActive = async (rep: Representative, e?: React.MouseEvent) => {
    e?.stopPropagation();
    await api(`/admin/representatives/${rep._id}`, { method: 'PATCH', body: JSON.stringify({ active: !rep.active }) });
    refreshAll();
  };

  const markPaid = async (productId?: string) => {
    if (!detailId) return;
    setPaying(true);
    try {
      await api(`/admin/representatives/${detailId}/mark-paid`, {
        method: 'PATCH',
        body: JSON.stringify({ productId, note: payNote.trim() }),
      });
      setPayNote('');
      refreshAll();
    } finally {
      setPaying(false);
    }
  };

  const cancelPayout = async (payoutId: string) => {
    if (!detailId) return;
    setCancelingPayoutId(payoutId);
    try {
      await api(`/admin/representatives/${detailId}/payouts/${payoutId}`, { method: 'DELETE' });
      setConfirmCancelId(null);
      refreshAll();
    } finally {
      setCancelingPayoutId(null);
    }
  };

  const totalPending = (rep: Representative) => rep.products.reduce((sum, p) => sum + p.totalPendingCents, 0);

  return (
    <div className="ras-pane">
      {/* ── Cards de resumo geral ── */}
      <div className="rrp-summary-grid">
        <button className="rrp-summary-card rrp-summary-card--tier1" onClick={() => setRankingOpen(true)}>
          <TrendingUp size={18} />
          <div className="rrp-summary-value">{summary?.totalSales ?? '—'}</div>
          <div className="rrp-summary-label">vendas via embaixadores</div>
        </button>
        <div className="rrp-summary-card rrp-summary-card--static rrp-summary-card--tier2">
          <DollarSign size={18} />
          <div className="rrp-summary-value">{summary ? cents(summary.totalRevenueCents) : '—'}</div>
          <div className="rrp-summary-label">receita gerada por embaixadores</div>
        </div>
        <div className="rrp-summary-card rrp-summary-card--static rrp-summary-card--highlight">
          <PiggyBank size={18} />
          <div className="rrp-summary-value">{summary ? cents(summary.netProfitCents) : '—'}</div>
          <div className="rrp-summary-label">
            seu lucro líquido
            <span className="rrp-summary-tooltip" title="Receita gerada por embaixadores menos o total de comissões pagas/devidas a eles.">
              <Info size={11} />
            </span>
          </div>
        </div>
      </div>

      {/* ── Cadastro ── */}
      <div className="ras-section">
        <div className="ras-section-header">
          <div className="ras-section-title">
            Embaixadores
            {currentCycle && <span className="rrp-cycle-badge">Ciclo #{currentCycle.number} vigente</span>}
          </div>
          <div className="rrp-header-actions">
            <button className="ras-grant-btn" onClick={openCreate}>
              <Plus size={14} /> Cadastrar embaixador
            </button>
            {!confirmCloseCycle ? (
              <button className="ras-btn-modal-close" onClick={() => setConfirmCloseCycle(true)}>
                Encerrar ciclo atual
              </button>
            ) : (
              <div className="rrp-payout-confirm">
                <span>Encerrar ciclo #{currentCycle?.number}? Afeta todos os embaixadores de uma vez.</span>
                <button className="ras-confirm-btn" onClick={handleCloseCycle} disabled={closingCycle}>
                  {closingCycle ? 'Encerrando…' : 'Confirmar'}
                </button>
                <button className="ras-icon-btn" onClick={() => setConfirmCloseCycle(false)}><X size={13} /></button>
              </div>
            )}
            <button type="button" className="rrp-reset-cycles-btn" onClick={openResetCycles} title="Apaga o histórico de ciclos e recomeça — não mexe nas comissões já geradas">
              Resetar ciclos
            </button>
          </div>
        </div>
      </div>

      {/* ── Grid de embaixadores ── */}
      <div className="ras-section" style={{ padding: '12px 6px' }}>
        {loading && <p className="rrp-empty">Carregando…</p>}
        {!loading && reps.length === 0 && <p className="rrp-empty">Nenhum embaixador cadastrado.</p>}
        <div className="rrp-grid">
          {reps.map(rep => (
            <button key={rep._id} className="rrp-rep-card" onClick={() => openDetail(rep._id)}>
              <div className="rrp-rep-card-top">
                <div className="rrp-rep-card-name">{rep.name}</div>
                <span
                  className={`rrp-status rrp-rep-card-toggle ${rep.active ? 'rrp-status--active' : 'rrp-status--inactive'}`}
                  onClick={e => handleToggleActive(rep, e)}
                  title={rep.active ? 'Clique para desativar' : 'Clique para ativar'}
                >
                  {rep.active ? 'Ativo' : 'Inativo'}
                </span>
              </div>
              <div className="rrp-rep-card-products">
                {rep.products.length === 0 ? 'Nenhum produto vinculado' : rep.products.map(p => p.title).join(' · ')}
              </div>
              <div className="rrp-rep-card-bottom">
                <span className="rrp-rep-card-pending">{cents(totalPending(rep))} pendente</span>
                <span className="rrp-rep-card-edit" onClick={e => openEdit(rep, e)}><Edit2 size={13} /></span>
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* ── Modal: cadastro/edição de embaixador ── */}
      {formOpen && ReactDOM.createPortal(
        <div className="ras-modal-overlay" onClick={closeForm}>
          <div className="ras-modal" onClick={e => e.stopPropagation()}>
            <div className="ras-modal-header">
              <div className="ras-modal-title">{editingId ? 'Editar embaixador' : 'Cadastrar embaixador'}</div>
              <button className="ras-modal-close" onClick={closeForm}><X size={18} /></button>
            </div>
            <div className="ras-modal-body">
              <div className="ras-modal-section">
                <div className="ras-form-row">
                  <input className="ras-input" placeholder="Nome" value={form.name}
                    onChange={e => setForm(f => ({ ...f, name: e.target.value }))} />
                  {!editingId && (
                    <div className="rrp-email-autocomplete">
                      <input className="ras-input" placeholder="Email" value={form.email}
                        autoComplete="off"
                        onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
                        onFocus={() => { if (emailSuggestions.length > 0) setEmailSuggestionsOpen(true); }}
                        onBlur={() => setTimeout(() => setEmailSuggestionsOpen(false), 150)} />
                      {emailSuggestionsOpen && (
                        <div className="rrp-email-suggestions">
                          {emailSuggestions.map(u => (
                            <button
                              key={u._id}
                              type="button"
                              className="rrp-email-suggestion-row"
                              // onMouseDown (não onClick) dispara ANTES do onBlur do
                              // input — sem isso, o blur fecharia a lista antes do
                              // clique ser processado.
                              onMouseDown={() => selectEmailSuggestion(u)}
                            >
                              <span className="rrp-email-suggestion-email">{u.email}</span>
                              {u.username && <span className="rrp-email-suggestion-name">{u.username}</span>}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
                <div className="ras-form-row">
                  <input className="ras-input"
                    placeholder={editingId ? 'Código de indicação' : 'Código de indicação (opcional, gerado automaticamente)'}
                    value={form.referralCode}
                    onChange={e => {
                      setForm(f => ({ ...f, referralCode: e.target.value }));
                      if (confirmCodeChange) setConfirmCodeChange(false);
                    }} />
                </div>
                {codeChanged && confirmCodeChange && (
                  <div className="ras-msg ras-msg--warn">
                    Atenção: links já compartilhados com o código atual (<strong>{editingOriginalCode}</strong>) deixarão
                    de funcionar após esta mudança — inclusive quem clicou um link antigo nos últimos 30 dias e ainda
                    não comprou. Isso NÃO afeta vendas já aprovadas nem solicitações em andamento (migradas
                    automaticamente pro código novo).
                  </div>
                )}
              </div>

              <div className="ras-modal-section">
                <div className="ras-modal-section-label">Produtos representados</div>
                <div className="rrp-product-picker">
                  {products.map(p => (
                    <div key={p._id} className="rrp-picker-row">
                      <label className="ras-toggle-label">
                        <input type="checkbox" checked={!!form.selected[p._id]} onChange={() => toggleProduct(p._id)} />
                        {p.title}
                      </label>
                      {form.selected[p._id] && (
                        <label className="ras-toggle-label rrp-picker-free">
                          <input type="checkbox" checked={!!form.freeAccess[p._id]} onChange={() => toggleFreeAccess(p._id)} />
                          Acesso gratuito
                        </label>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              <div className="ras-modal-section">
                <div className="ras-modal-section-label">Comissão</div>
                <p className="rrp-tier-hint">Percentual pago ao embaixador conforme o número de vendas já geradas por ele — vale igualmente para todos os produtos dele (cada produto conta suas próprias vendas). Deixe vazio para usar o padrão global (20% / 30% / 40% / 50%).</p>
                {form.commissionTiers.map((tier, i) => (
                  <div key={i} className="rrp-tier-row">
                    <input className="ras-input" type="number" min={1} placeholder="A partir da venda nº" value={tier.minSales}
                      onChange={e => updateTier(i, { minSales: Number(e.target.value) })} />
                    <input className="ras-input" type="number" min={0} max={100} placeholder="Comissão (%)" value={tier.percent}
                      onChange={e => updateTier(i, { percent: Number(e.target.value) })} />
                    <button type="button" className="ras-icon-btn" onClick={() => removeTier(i)} title="Remover tier"><X size={13} /></button>
                  </div>
                ))}
                <button type="button" className="ras-add-btn" onClick={addTier}><Plus size={14} /> Adicionar tier</button>
              </div>

              {errorMsg && <div className="ras-msg ras-msg--err">{errorMsg}</div>}
            </div>
            <div className="ras-modal-footer">
              {/* Exclusão só existe editando um embaixador já cadastrado, nunca
                  no cadastro novo — e fica isolada à esquerda (marginRight:auto),
                  separada fisicamente de Salvar/Cancelar pra reduzir clique
                  acidental numa ação que o backend só permite mesmo se o
                  embaixador nunca tiver gerado nenhuma comissão. */}
              {editingId && (
                confirmDelete ? (
                  <span className="rrp-payout-confirm" style={{ marginRight: 'auto' }}>
                    <span>Excluir este embaixador?</span>
                    <button className="rrp-delete-confirm-yes" onClick={handleDelete} disabled={deleting}>
                      {deleting ? 'Excluindo…' : 'Sim, excluir'}
                    </button>
                    <button className="ras-icon-btn" onClick={() => setConfirmDelete(false)} disabled={deleting}><X size={13} /></button>
                  </span>
                ) : (
                  <button
                    type="button" className="ras-icon-btn ras-icon-btn--danger" style={{ marginRight: 'auto' }}
                    onClick={() => setConfirmDelete(true)} title="Excluir embaixador"
                  >
                    <Trash2 size={14} />
                  </button>
                )
              )}
              <button className="ras-grant-btn" onClick={handleSaveClick} disabled={saving}>
                {saving
                  ? 'Salvando…'
                  : codeChanged && confirmCodeChange
                    ? 'Confirmar mesmo assim'
                    : editingId ? 'Salvar alterações' : 'Cadastrar'}
              </button>
              <button className="ras-btn-modal-close" onClick={closeForm}><X size={13} /> Cancelar</button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* ── Modal: resetar ciclos — confirmação tipada (mesmo padrão da Zona
          de Risco em Configurações), já que é uma ação global que afeta
          todos os embaixadores de uma vez. ── */}
      {resetCyclesOpen && ReactDOM.createPortal(
        <div className="ras-modal-overlay" onClick={closeResetCycles}>
          <div className="ras-modal" onClick={e => e.stopPropagation()}>
            <div className="ras-modal-header">
              <div>
                <div className="ras-modal-title">Resetar ciclos</div>
                <div className="ras-modal-subtitle">Isso afeta todos os embaixadores de uma vez.</div>
              </div>
              <button className="ras-modal-close" onClick={closeResetCycles}><X size={18} /></button>
            </div>
            <div className="ras-modal-body">
              <div className="ras-modal-section">
                <ul className="rrp-reset-cycles-list">
                  <li><strong>Apaga:</strong> o ciclo atual (#{currentCycle?.number ?? '—'}) e todo o histórico de ciclos fechados, os resultados de tier por ciclo e o piso de carry-over de cada embaixador.</li>
                  <li><strong>NÃO apaga:</strong> nenhuma comissão já gerada (paga ou pendente) nem pagamentos já registrados — o ganho real de cada venda continua intacto e consultável.</li>
                  <li>Um ciclo novo é aberto em seguida. O número não é necessariamente #1 — só é quando não existe nenhuma comissão no histórico, pra nunca misturar contagem de vendas antigas com o ciclo atual.</li>
                </ul>
              </div>
              <div className="ras-modal-section">
                <div className="ras-modal-section-label">Para confirmar, digite <strong>RESETAR</strong> abaixo</div>
                <input className="ras-input" value={resetConfirmText} onChange={e => setResetConfirmText(e.target.value)} placeholder="RESETAR" />
              </div>
              {resetError && <div className="ras-msg ras-msg--err">{resetError}</div>}
            </div>
            <div className="ras-modal-footer">
              <button className="ras-btn-modal-close" onClick={closeResetCycles}>Cancelar</button>
              <button
                className="rrp-delete-confirm-yes"
                onClick={handleResetCycles}
                disabled={resetting || resetConfirmText.trim().toUpperCase() !== 'RESETAR'}
              >
                {resetting ? 'Resetando…' : 'Resetar definitivamente'}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* ── Modal: ranking de vendas ── */}
      {rankingOpen && summary && ReactDOM.createPortal(
        <div className="ras-modal-overlay" onClick={() => setRankingOpen(false)}>
          <div className="ras-modal" onClick={e => e.stopPropagation()}>
            <div className="ras-modal-header">
              <div className="ras-modal-title">Ranking de embaixadores</div>
              <button className="ras-modal-close" onClick={() => setRankingOpen(false)}><X size={18} /></button>
            </div>
            <div className="ras-modal-body">
              <div className="ras-modal-section">
                {summary.ranking.length === 0 && <div className="ras-modal-empty">Nenhuma venda registrada ainda.</div>}
                {summary.ranking.map(r => (
                  <div key={r.representativeId} className="rrp-ranking-row">
                    <span className="rrp-ranking-pos">{r.position}º</span>
                    <span className="rrp-ranking-name">{r.name}</span>
                    <span className="rrp-ranking-sales">{r.salesCount} venda{r.salesCount !== 1 ? 's' : ''}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* ── Modal: detalhe do embaixador ── */}
      {detailId && ReactDOM.createPortal(
        <div className="ras-modal-overlay" onClick={closeDetail}>
          <div className="ras-modal rrp-detail-modal" onClick={e => e.stopPropagation()}>
            <div className="rrp-detail-header">
              <div className="rrp-detail-header-info">
                <div className="rrp-detail-header-name">{detail?.name || '…'}</div>
                {detail && (
                  <div className="rrp-detail-header-meta">
                    {detail.email} · código {detail.referralCode} · Ciclo #{detail.currentCycle.number} vigente
                  </div>
                )}
              </div>
              <button className="rrp-detail-header-close" onClick={closeDetail}><X size={18} /></button>

              <nav className="rrp-detail-tabs">
                <button className={`rrp-detail-tab${detailTab === 'overview' ? ' rrp-detail-tab--active' : ''}`} onClick={() => setDetailTab('overview')}>
                  <LayoutDashboard size={15} /> Visão geral
                </button>
                <button className={`rrp-detail-tab${detailTab === 'products' ? ' rrp-detail-tab--active' : ''}`} onClick={() => setDetailTab('products')}>
                  <Package size={15} /> Produtos
                </button>
                <button className={`rrp-detail-tab${detailTab === 'payments' ? ' rrp-detail-tab--active' : ''}`} onClick={() => setDetailTab('payments')}>
                  <Receipt size={15} /> Pagamentos
                </button>
              </nav>
            </div>

            <div className="ras-modal-body">
              {detailLoading && <div className="ras-modal-empty">Carregando…</div>}

              {detail && detailTab === 'overview' && (
                <div className="rrp-overview-grid">
                  <div className="rrp-overview-card">
                    <ShoppingCart size={18} />
                    <div className="rrp-overview-value">{detail.summary.totalSales}</div>
                    <div className="rrp-overview-label">vendas totais</div>
                  </div>
                  <div className="rrp-overview-card rrp-overview-card--mint">
                    <DollarSign size={18} />
                    <div className="rrp-overview-value">{cents(detail.summary.totalRevenueCents)}</div>
                    <div className="rrp-overview-label">receita total</div>
                  </div>
                  <div className="rrp-overview-card rrp-overview-card--blue">
                    <PiggyBank size={18} />
                    <div className="rrp-overview-value">{cents(detail.summary.totalProfitCents)}</div>
                    <div className="rrp-overview-label">lucro que ele te deu</div>
                  </div>
                  <div className="rrp-overview-card rrp-overview-card--highlight">
                    <Wallet size={18} />
                    <div className="rrp-overview-value">{cents(detail.summary.totalPendingCents)}</div>
                    <div className="rrp-overview-label">saldo pendente</div>
                  </div>
                </div>
              )}

              {detail && detailTab === 'products' && (() => {
                const periods = Array.from(new Set(detail.products.map(p => p.periodo).filter((p): p is number => p != null))).sort((a, b) => a - b);
                const filteredProducts = productPeriodFilter === null
                  ? detail.products
                  : detail.products.filter(p => p.periodo === productPeriodFilter);
                return (
                  <div className="ras-modal-section">
                    <div className="ras-modal-section-label">Produtos</div>

                    {periods.length > 0 && (
                      <div className="rrp-period-filter">
                        <button
                          className={`rrp-period-pill${productPeriodFilter === null ? ' rrp-period-pill--active' : ''}`}
                          onClick={() => setProductPeriodFilter(null)}
                        >
                          Todos
                        </button>
                        {periods.map(p => (
                          <button
                            key={p}
                            className={`rrp-period-pill${productPeriodFilter === p ? ' rrp-period-pill--active' : ''}`}
                            onClick={() => setProductPeriodFilter(p)}
                          >
                            {p}º período
                          </button>
                        ))}
                      </div>
                    )}

                    {filteredProducts.length === 0 && <div className="ras-modal-empty">Nenhum produto neste período.</div>}

                    <div className="rrp-detail-list">
                      {filteredProducts.map(p => (
                        <div key={p.productId} className="rrp-detail-product">
                          <div className="rrp-detail-product-top">
                            <span className="rrp-detail-product-title">{p.title}</span>
                            {p.periodo != null && <span className="rrp-period-badge">{p.periodo}º período</span>}
                            {p.freeAccess && <span className="rrp-free-badge">acesso grátis</span>}
                          </div>
                          <div className="rrp-detail-stats">
                            <span>{p.salesCount} vendas (total)</span>
                            <span>{p.salesCountThisCycle} vendas neste ciclo</span>
                            <span>{p.currentTierPercent}% tier atual</span>
                            <span>{cents(p.totalEarnedCents)} ganho</span>
                            <span className="rrp-detail-pending">{cents(p.totalPendingCents)} pendente</span>
                          </div>

                          {p.carryOverPercent != null && (
                            <div className="rrp-carryover-note">
                              Começou este ciclo já em {p.carryOverPercent}% por carry-over do ciclo anterior.
                            </div>
                          )}

                          <div className="rrp-detail-roadmap">
                            {p.tiers.map(t => (
                              <div key={t.minSales} className={`rrp-detail-tier${t.reached ? ' reached' : ''}${t.current ? ' current' : ''}`}>
                                <span>{t.percent}%</span>
                                <small>venda {t.minSales}+</small>
                              </div>
                            ))}
                          </div>

                          {p.totalPendingCents > 0 && (
                            <button className="ras-grant-btn rrp-mark-paid-btn" onClick={() => markPaid(p.productId)} disabled={paying}>
                              Marcar este produto como pago
                            </button>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })()}

              {detail && detailTab === 'payments' && (
                <>
                  <div className="ras-modal-section">
                    <div className="ras-modal-section-label">Marcar novo pagamento</div>
                    <div className="rrp-pay-form">
                      <input className="ras-input" placeholder="Observação (opcional, ex: PIX enviado dia 10)"
                        value={payNote} onChange={e => setPayNote(e.target.value)} />
                      <button className="ras-grant-btn" onClick={() => markPaid(undefined)} disabled={paying}>
                        {paying ? 'Registrando…' : 'Quitar todas as pendências'}
                      </button>
                    </div>
                  </div>

                  <div className="ras-modal-section">
                    <div className="ras-modal-section-label">Histórico de pagamentos</div>
                    {detail.payoutHistory.length === 0 && <div className="ras-modal-empty">Nenhum pagamento registrado ainda.</div>}
                    {detail.payoutHistory.map(payout => (
                      <div key={payout._id} className="rrp-payout-row">
                        <div>
                          <div className="rrp-payout-amount">{cents(payout.amountCents)}</div>
                          <div className="rrp-payout-meta">
                            {payout.salesCount} venda{payout.salesCount !== 1 ? 's' : ''} · {fmtDate(payout.paidAt)}
                            {payout.legacy && ' · pagamento legado'}
                          </div>
                          {payout.note && <div className="rrp-payout-note">{payout.note}</div>}
                        </div>

                        {confirmCancelId === payout._id ? (
                          <div className="rrp-payout-confirm">
                            <span>Cancelar este pagamento?</span>
                            <button className="ras-confirm-btn" onClick={() => cancelPayout(payout._id)} disabled={cancelingPayoutId === payout._id}>
                              {cancelingPayoutId === payout._id ? 'Cancelando…' : 'Sim'}
                            </button>
                            <button className="ras-icon-btn" onClick={() => setConfirmCancelId(null)}><X size={13} /></button>
                          </div>
                        ) : (
                          <button className="rrp-payout-cancel-btn" onClick={() => setConfirmCancelId(payout._id)} title="Cancelar pagamento">
                            <Trash2 size={14} />
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default RyanRepresentatives;
